import type { Timestamp } from './timestamp';

export const MODULE_REGISTRY = [
  { key: 'enrollment',     i18nKey: 'modules.enrollment',     icon: '📝' },
  { key: 'fees',           i18nKey: 'modules.fees',           icon: '💰' },
  { key: 'classes',        i18nKey: 'modules.classes',        icon: '🏫' },
  { key: 'assignments',    i18nKey: 'modules.assignments',    icon: '📋' },
  { key: 'schedule',       i18nKey: 'modules.schedule',       icon: '📅' },
  { key: 'emergencyCards', i18nKey: 'modules.emergencyCards', icon: '🚨' },
] as const;

export type ModuleRegistryEntry = typeof MODULE_REGISTRY[number];

export type ModuleKey = ModuleRegistryEntry['key'];

export const MODULE_KEYS: readonly ModuleKey[] =
  MODULE_REGISTRY.map((m) => m.key) as readonly ModuleKey[];

export function isValidModuleKey(k: string): k is ModuleKey {
  return (MODULE_KEYS as readonly string[]).includes(k);
}

export const SCHOOL_YEAR_PATTERN = /^\d{4}-\d{4}$/;

export function isValidSchoolYear(y: string): boolean {
  if (!SCHOOL_YEAR_PATTERN.test(y)) return false;
  const [start, end] = y.split('-').map(Number);
  return end === start + 1;
}

/**
 * Датите на една учебна година.
 *
 * 🔴 Краят на учебната година е ВЪВЕДЕН, не изведен (C4 R1). Дотук той се смяташе от
 * началото, броя седмици и ваканциите — тоест беше РЕЗУЛТАТ, различен за всеки курс, и
 * затова не можеше да служи за граница. Сега учебната година е рамката: въведена двойка
 * дати, извън която курс не се записва.
 *
 * Двете живеят заедно: ред без край не е рамка и не ограничава нищо, затова полето е
 * задължително за всеки ЗАПИСАН ред. Четенето остава предпазливо (`isRealIsoDate` при
 * всеки консуматор) — типът описва какво пишем НИЕ, а Firestore се редактира и пряко.
 *
 * Работната година е оста на ПЕРСОНАЛА: подготовка преди началото на занятията и дейност
 * след края им. След C4 тя няма отношение към КУРСОВЕТЕ (остава за задача D). Прозорецът,
 * в който ваканция има право да съществува, се строи така, че да е НАДМНОЖЕСТВО на
 * учебната година (R2) — виж `holidayWindow`.
 */
export interface SchoolYearDates {
  /** Първият ден, от който тръгват занятията, ISO `YYYY-MM-DD`. */
  schoolYearStartDate: string;
  /** Последният ден на учебната година, ISO `YYYY-MM-DD`. Въведен, не изведен (R1). */
  schoolYearEndDate: string;
  /** Начало на работната година (≤ учебната). */
  workYearStartDate?: string;
  /** Край на работната година (≥ учебната). Липсва → прагът е 30 юни. */
  workYearEndDate?: string;
  /**
   * Платена подготовка от началото на работната година до първото занятие (TASK_46, owner
   * 02.10.2026). ВКЛ → подготвителните дни на всеки слот влизат в очакваните часове на
   * декларацията и в договореното (вкл. помощниците); календарите не се променят. Липсва или
   * `false` → поведението отпреди. Прозорецът е `preparationWindow` (`schoolCalendar.ts`).
   *
   * 🔴 Ново поле тук значи нов ред в `compactDates` (`systemSettings.ts`) и в `isUntouched`
   * (`schoolYearDatesPayload.ts`) — пропуснато там, то изчезва ТИХО при запис.
   */
  paidPreparation?: boolean;
}

export interface SchoolSettings {
  activeSchoolYear: string;
  moduleYearOverrides?: Partial<Record<ModuleKey, string>>;
  supportedSchoolYears: string[];
  /**
   * Дати по учебна година; ключът е каноничният формат с тире („2026-2027").
   *
   * Карта, а не едно поле: `payrollPrepare` се вика с явна година, тоест преизчисляване за
   * миналата година трябва да чете НЕЙНОТО начало, а не текущото. Липсваща карта = всичко
   * работи по резервата (1 септември), както преди да съществува.
   */
  schoolYears?: Record<string, SchoolYearDates>;
  updatedAt: Timestamp;
  updatedBy: string;
}

export interface SaveSchoolSettingsRequest {
  activeSchoolYear?: string;
  moduleYearOverrides?: Partial<Record<ModuleKey, string | null>>;
  supportedSchoolYears?: string[];
  /** `null` за година трие нейния запис; празни полета не се пишат като празни низове. */
  schoolYears?: Record<string, SchoolYearDates | null>;
}

export interface GetActiveSchoolSettingsResponse {
  settings: SchoolSettings | null;
}
